import * as portals from "react-reverse-portal";

import { DEFAULT_CHART_STATE } from "constants/chart.constants";
import type { ISelectedWell } from "stores/map";
import styled from "styled-components/macro";

import type { ChartSettings } from "hooks/charts";

import { GridWidget } from "models/dashboard";
import type { ThreeDViewerPersistedStateT } from "models/vis";

import { TypeWellDetailsWidget } from "components/arps/widget/TypeWellDetails/TypeWellDetailsWidget";
import { TypeWellEditorWidget } from "components/arps/widget/TypeWellEditor/TypeWellEditorWidget";
import { AutomationWidget } from "components/automation/AutomationWidget";
import { ChartWrapper as ChartWrapperBeta } from "components/charts/components";
import { RigFracChartWrapper } from "components/charts/rig-frac";
import { WellStatusChartWrapper } from "components/charts/well-status";
import { DataTable, TableProvider } from "components/data-table";
import ExploitableReport from "components/exploitable-report/ExploitableReport";
import Forecasting from "components/forecasting/Forecasting";
import { ForecastingProvider } from "components/forecasting/contexts/ForecastingProvider";
import { GeoMap } from "components/geo-map/GeoMap";
import { GeoMapDataTableWidget } from "components/geo-map/GeoMapDataTable";
import { GroupByProvider } from "components/groupBy/group-by.context";
import Map from "components/map/Map";
import { IpdbStoreProvider } from "components/map/contexts/IpdbStoreProvider";
import { MapProvider } from "components/map/contexts/MapContext";
import MidstreamNavigatorWidget from "components/midstream/midstream-navigator/MidstreamNavigatorWidget";
import FeatureImportance from "components/ml/FeatureImportance";
import { MultiPhaseChart } from "components/multiphase-chart/components";
import MPCSpecificSettingsWrapper from "components/multiphase-chart/components/chart/MPCSpecificSettingsWrapper";
import { PadScenarioProvider } from "components/pad-generator/contexts/PadScenarioProvider";
import RFvsPVChart from "components/sam/RFvsPVChart";
import SamCharts from "components/sam/SamCharts";
import SamTable from "components/sam/SamTable";
import { ChartProvider } from "components/shared/charts";
import {
  Info,
  ThreeDViewer,
  TypeLogStoreProvider,
  TypeLogWidget,
  WellCard,
  WellCompletions3dView,
  WellDetailsStoreProvider,
  WellSurveyVisualizer,
  XdaSettingsStoreProvider,
  XdaViewer
} from "components/vis";

import { ChartsProvider } from "../../contexts";
import { EntityKind } from "../../models/entityKind";
import ChartSettingsVsGlobalSettings from "../charts/components/ChartSettingsVsGlobalSettings";
import { WellDataAnalyzerWidget } from "../data-analyzer/components";
import { ChartSettingsProvider as MultiPhaseChartSettingsProvider } from "../multiphase-chart/context";
import PadScenario from "../pad-generator/PadScenario";
import { RigFracDataTableWidget } from "./RigFracDataTable/RigFracDataTableWidget";
import {
  AutomationWidgetKey,
  ChartWidgetKey,
  FeatureImportanceWidgetKey,
  ForecastingWidgetKey,
  GeoMapDataTableWidgetKey,
  MidstreamChartWidgetKey,
  MidstreamDataTableWidgetKey,
  MidstreamNavigatorWidgetKey,
  PadScenarioWidgetKey,
  RigFracChartWidgetKey,
  RigFracDataTableWidgetKey,
  TypeWellDetailsWidgetKey,
  TypeWellEditorWidgetKey,
  WellDataAnalyzer,
  WellStatusChartWidgetKey
} from "./constants/widgets.constants";

export interface WidgetInputModel {
  widget: GridWidget;
  showFullscreen: boolean;
  selectedWells: [];
  wells: Record<string, ISelectedWell>;
  onFullscreen: (b: boolean) => void;
  refreshKey?: string;
}
function Widget(input: WidgetInputModel) {
  if (!input.widget?.component) return null;
  const firstSelectedUwi = Object.keys(input.selectedWells)[0];

  const fullScreenMarkup = !input.showFullscreen && (
    <Wrapper className="portal">
      <portals.OutPortal node={input.widget.componentOptions?.portalNode} />
    </Wrapper>
  );
  return (
    <>
      {input.widget.component === "map" && (
        <Wrapper>
          <portals.InPortal node={input.widget.componentOptions.portalNode}>
            <Map
              id={input.widget.name}
              onFullscreen={(fullscreen) => input.onFullscreen(fullscreen)}
            />
          </portals.InPortal>
          {fullScreenMarkup}
        </Wrapper>
      )}

      {input.widget.component === "xda" && (
        <IpdbStoreProvider>
          <XdaSettingsStoreProvider
            key={input.refreshKey}
            initialState={input.widget.componentState}>
            <Wrapper>
              <portals.InPortal node={input.widget.componentOptions.portalNode}>
                <XdaViewer
                  id={input.widget.ref}
                  widgetType={input.widget.component}
                  onFullscreenToggle={(fullscreen) => input.onFullscreen(fullscreen)}
                />
              </portals.InPortal>
              {fullScreenMarkup}
            </Wrapper>
          </XdaSettingsStoreProvider>
        </IpdbStoreProvider>
      )}

      {input.widget.component === "threed" && (
        <IpdbStoreProvider>
          <XdaSettingsStoreProvider
            key={input.refreshKey}
            initialState={input.widget.componentState}>
            <Wrapper>
              <portals.InPortal node={input.widget.componentOptions.portalNode}>
                <ThreeDViewer
                  id={input.widget.ref}
                  // A save predating persistence is partial; the viewer reads each field guarded.
                  initialState={
                    input.widget.componentState as ThreeDViewerPersistedStateT
                  }
                  onFullscreenToggle={(fullscreen) => input.onFullscreen(fullscreen)}
                />
              </portals.InPortal>
              {fullScreenMarkup}
            </Wrapper>
          </XdaSettingsStoreProvider>
        </IpdbStoreProvider>
      )}

      {input.widget.component === "data-table" && (
        <TableProvider entityKind={EntityKind.Well}>
          <DataTable id={input.widget.ref} initialState={input.widget.componentState} />
        </TableProvider>
      )}
      {input.widget.component === MidstreamDataTableWidgetKey && (
        <TableProvider entityKind={EntityKind.Facility}>
          <DataTable id={input.widget.ref} initialState={input.widget.componentState} />
        </TableProvider>
      )}

      {input.widget.component === ChartWidgetKey && (
        <Wrapper>
          <portals.InPortal node={input.widget.componentOptions.portalNode}>
            <ChartsProvider
              id={input.widget.ref}
              entityKind={EntityKind.Well}
              initialChartSettings={input.widget.componentState}>
              <ChartWrapperBeta
                onFullscreen={(fullscreen) => input.onFullscreen(fullscreen)}
              />
              <ChartSettingsVsGlobalSettings message="Chart specific settings" />
            </ChartsProvider>
          </portals.InPortal>
          {fullScreenMarkup}
        </Wrapper>
      )}

      {input.widget.component === RigFracChartWidgetKey && (
        <Wrapper>
          <portals.InPortal
            node={input.widget.componentOptions.portalNode as portals.HtmlPortalNode}>
            <ChartsProvider
              id={input.widget.ref}
              entityKind={EntityKind.Well}
              initialChartSettings={
                input.widget.componentState as unknown as ChartSettings
              }>
              <RigFracChartWrapper
                onFullscreen={(fullscreen) => input.onFullscreen(fullscreen)}
              />
            </ChartsProvider>
          </portals.InPortal>
          {fullScreenMarkup}
        </Wrapper>
      )}

      {input.widget.component === WellStatusChartWidgetKey && (
        <Wrapper>
          <portals.InPortal
            node={input.widget.componentOptions.portalNode as portals.HtmlPortalNode}>
            <ChartsProvider
              id={input.widget.ref}
              entityKind={EntityKind.Well}
              initialChartSettings={
                input.widget.componentState as unknown as ChartSettings
              }>
              <WellStatusChartWrapper
                onFullscreen={(fullscreen) => input.onFullscreen(fullscreen)}
              />
            </ChartsProvider>
          </portals.InPortal>
          {fullScreenMarkup}
        </Wrapper>
      )}

      {input.widget.component === MidstreamChartWidgetKey && (
        <Wrapper>
          <portals.InPortal node={input.widget.componentOptions.portalNode}>
            <ChartsProvider
              id={input.widget.ref}
              entityKind={EntityKind.Facility}
              initialChartSettings={input.widget.componentState}>
              <ChartWrapperBeta
                onFullscreen={(fullscreen) => input.onFullscreen(fullscreen)}
              />
              <ChartSettingsVsGlobalSettings message="Chart specific settings" />
            </ChartsProvider>
          </portals.InPortal>
          {fullScreenMarkup}
        </Wrapper>
      )}

      {input.widget.component === "info" && (
        <Info
          id={input.widget.ref}
          selectedWell={firstSelectedUwi}
          initialPropertyName={input.widget.componentState["AggregateField"]}
          initialForecastToggle={input.widget.componentState["IsForecastToggleOn"]}
          initialForecastFolder={input.widget.componentState["SelectedForecastFolder"]}
        />
      )}

      {input.widget.component === "well-card" && (
        <GroupByProvider>
          <WellDetailsStoreProvider>
            <WellCard entityKind={EntityKind.Well} />
          </WellDetailsStoreProvider>
        </GroupByProvider>
      )}

      {input.widget.component === "type-log" && (
        <GroupByProvider>
          <MapProvider>
            <TypeLogStoreProvider>
              <TypeLogWidget />
            </TypeLogStoreProvider>
          </MapProvider>
        </GroupByProvider>
      )}

      {input.widget.component === "facility-ticket" && (
        <GroupByProvider>
          <WellDetailsStoreProvider>
            <WellCard entityKind={EntityKind.Facility} />
          </WellDetailsStoreProvider>
        </GroupByProvider>
      )}

      {input.widget.component === "xdaviewer" && (
        <IpdbStoreProvider>
          <XdaSettingsStoreProvider
            key={input.refreshKey}
            initialState={input.widget.componentState}>
            <Wrapper>
              <portals.InPortal node={input.widget.componentOptions.portalNode}>
                <XdaViewer
                  id={input.widget.ref}
                  widgetType={input.widget.component}
                  onFullscreenToggle={(fullscreen) => input.onFullscreen(fullscreen)}
                />
              </portals.InPortal>
              {fullScreenMarkup}
            </Wrapper>
          </XdaSettingsStoreProvider>
        </IpdbStoreProvider>
      )}

      {input.widget.component === "survey-vis" && (
        <Wrapper>
          <portals.InPortal node={input.widget.componentOptions.portalNode}>
            <WellSurveyVisualizer
              // wells carries the map's current colour; selectedWells only has
              // the colour from when the well was clicked.
              well={
                input.wells[firstSelectedUwi] ?? input.selectedWells[firstSelectedUwi]
              }
              onFullscreenToggle={(fullscreen) => input.onFullscreen(fullscreen)}
            />
          </portals.InPortal>
          {fullScreenMarkup}
        </Wrapper>
      )}

      {input.widget.component === "completion" && (
        <Wrapper>
          <portals.InPortal node={input.widget.componentOptions.portalNode}>
            <WellCompletions3dView
              uwid={input.wells ? Object.keys(input.wells)[0] : ""}
              onFullscreenToggle={(fullscreen) => input.onFullscreen(fullscreen)}
            />
          </portals.InPortal>
          {fullScreenMarkup}
        </Wrapper>
      )}
      {input.widget.component === "exploitable-report" && <ExploitableReport />}
      {input.widget.component === "sam" && (
        <ChartProvider initialState={DEFAULT_CHART_STATE}>
          <SamCharts id={input.widget.ref} />
        </ChartProvider>
      )}
      {input.widget.component === "sam-table" && <SamTable />}
      {input.widget.component === "sam-rf-pv" && (
        <ChartProvider initialState={DEFAULT_CHART_STATE}>
          <RFvsPVChart id={input.widget.ref} />
        </ChartProvider>
      )}
      {input.widget.component === "multiphase-chart" && (
        <Wrapper>
          <portals.InPortal node={input.widget.componentOptions.portalNode}>
            <MultiPhaseChartSettingsProvider initialState={input.widget.componentState}>
              <MultiPhaseChart
                id={input.widget.ref}
                onFullscreenToggle={(fullscreen) => input.onFullscreen(fullscreen)}
                type={input.widget.component}
              />
              <MPCSpecificSettingsWrapper message="Chart-specific settings" />
            </MultiPhaseChartSettingsProvider>
          </portals.InPortal>
          {fullScreenMarkup}
        </Wrapper>
      )}
      {input.widget.component === TypeWellEditorWidgetKey && (
        <Wrapper>
          <TypeWellEditorWidget />
        </Wrapper>
      )}
      {input.widget.component === TypeWellDetailsWidgetKey && (
        <Wrapper>
          <TypeWellDetailsWidget />
        </Wrapper>
      )}

      {input.widget.component == PadScenarioWidgetKey && (
        <Wrapper>
          <PadScenarioProvider>
            <PadScenario />
          </PadScenarioProvider>
        </Wrapper>
      )}
      {input.widget.component == ForecastingWidgetKey && (
        <Wrapper>
          <ForecastingProvider>
            <Forecasting />
          </ForecastingProvider>
        </Wrapper>
      )}
      {input.widget.component === "geo-map" && (
        <Wrapper>
          <GeoMap />
        </Wrapper>
      )}
      {input.widget.component === WellDataAnalyzer && (
        <Wrapper>
          <WellDataAnalyzerWidget />
        </Wrapper>
      )}

      {input.widget.component === FeatureImportanceWidgetKey && (
        <Wrapper>
          <portals.InPortal node={input.widget.componentOptions.portalNode}>
            <FeatureImportance
              onFullscreenToggle={(fullscreen) => input.onFullscreen(fullscreen)}
            />
          </portals.InPortal>
          {fullScreenMarkup}
        </Wrapper>
      )}

      {input.widget.component === AutomationWidgetKey && (
        <Wrapper>
          <AutomationWidget />
        </Wrapper>
      )}

      {input.widget.component === MidstreamNavigatorWidgetKey && (
        <Wrapper>
          <MidstreamNavigatorWidget />
        </Wrapper>
      )}

      {input.widget.component === GeoMapDataTableWidgetKey && (
        <Wrapper>
          <GeoMapDataTableWidget />
        </Wrapper>
      )}

      {input.widget.component === RigFracDataTableWidgetKey && (
        <Wrapper>
          <RigFracDataTableWidget />
        </Wrapper>
      )}
    </>
  );
}

export default Widget;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
`;
